import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTranslation } from 'react-i18next';
import enUS from 'antd/locale/en_US';
import viVN from 'antd/locale/vi_VN';
import { FieldErrorSourceProvider } from '@ai-content-hub/ui/field';
import { UIProvider } from '@ai-content-hub/ui/providers';
import type { SupportedLocale } from '@ai-content-hub/ui/i18n';
import { setPromptLocale } from '@ai-content-hub/content-prompt';
import { setSharedComponentsLocale } from '@ai-content-hub/shared-components';

import { queryClient } from '@/lib/queryClient';
// EAA-1667: one wrapper mounts the shared core — theme, permissions, and the auth
// capability gate (derived from permissions) — replacing the former separate
// AuthCapabilityBridge + ThemeProvider. Every new app mounts its own CoreProvider.
import { WebContentCoreProvider } from '@/providers/WebContentCoreProvider';
import { AppRoutes } from '@/routes/AppRoutes';
import { webContentFieldErrorSource } from '@/components/webContentFieldErrorSource';
import { TenantRenameNotice } from '@/components/common/TenantRenameNotice';

// EAA-1666: ties packages/ui's UIProvider (component text + antd locale) to
// this app's own react-i18next language, so switching language propagates
// to every moved composite (DataTable, ActionButtons, uploads, ...) with no
// per-call-site work — without this, those components stayed English-only
// regardless of the app's active language.
const UILocaleBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { i18n } = useTranslation();
    const locale: SupportedLocale = i18n.language === 'vi' ? 'vi' : 'en';
    // Keep package-level catalogs (question-type labels, AI generator copy) in
    // sync with the app language so Gen Question and Import share one bilingual source.
    React.useEffect(() => {
        setPromptLocale(locale);
        setSharedComponentsLocale(locale);
    }, [locale]);
    return (
        <UIProvider locale={locale} antdLocale={locale === 'vi' ? viVN : enUS}>
            {children}
        </UIProvider>
    );
};

const App: React.FC = () => {
    return (
        // EAA-1670: all 10 slices are migrated off Redux Toolkit + Saga onto
        // Zustand + TanStack Query; the Redux Provider is retired.
        <QueryClientProvider client={queryClient}>
            <WebContentCoreProvider>
                <FieldErrorSourceProvider value={webContentFieldErrorSource}>
                    <UILocaleBridge>
                        {/* EAA-1988: mounted above the routes so the notice survives
                            navigation — a tenant held offline for a slug rename
                            refuses every screen, not one. */}
                        <TenantRenameNotice />
                        <AppRoutes />
                    </UILocaleBridge>
                </FieldErrorSourceProvider>
            </WebContentCoreProvider>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    );
};

export default App;
